import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild, signal, computed } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { InputTextModule } from 'primeng/inputtext';
import { AvatarModule } from 'primeng/avatar';
import { ButtonComponent } from '../../../@core/components/ui/button/button.component';
import { CheckboxComponent } from '../../../@core/components/ui/checkbox/checkbox.component';
import { IUser, SHARE_ACCESS } from '../../../@core/models/model';
import { USER_ROLE } from '../../../@core/models/enums';
import { UserService } from '../../../contract-management/services/user.service';
import { AuthenticationService } from '../../../@core/services/authentication.service';
import { UserAvatarPipe } from '../../pipes/user-avatar-pipe';
import { FullNamePipe } from '../../pipes/fullName.pipe';
import { ToastService } from '../../../@core/services/toast.service';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { AutoCompleteComponent } from '../../../@core/components/ui/autocomplete/autocomplete.component';
import { AutoCompleteOption } from '../../../@core/components/ui/autocomplete/autocomplete.model';
import { TRANSFER_OWNERSHIP } from 'src/app/common/svgs';
import { IconComponent } from 'src/app/@core/components/ui/icon/icon.component';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { LottieAnimationComponent } from '../lottie-animation/lottie-animation.component';

@Component({
    selector: 'app-transfer-ownership-dialog',
    standalone: true,
    imports: [
        CommonModule,
        DialogComponent,
        FormsModule,
        InputTextModule,
        AvatarModule,
        ButtonComponent,
        CheckboxComponent,
        UserAvatarPipe,
        FullNamePipe,
        IconFieldModule,
        InputIconModule,
        AutoCompleteComponent,
        IconComponent,
        LottieAnimationComponent
    ],
    templateUrl: './transfer-ownership-dialog.component.html',
    styleUrl: './transfer-ownership-dialog.component.scss'
})
export class TransferOwnershipDialogComponent implements OnInit {
    @Input() visible: boolean = false;
    @Input() userCount: number = 1; // Number of users selected for transfer
    @Input() isDefaultTransferDisabled: boolean = false; // Optional input to control if default transfer checkbox is disabled
    @Input() transferFunction?: (email: string) => Observable<any>; // Function to execute transfer
    @Output() visibleChange = new EventEmitter<boolean>();
    @Output() transferConfirmed = new EventEmitter<string>(); // Emits the email of the user to transfer to (for backward compatibility)
    @Output() transferCompleted = new EventEmitter<{ success: boolean; message?: string }>(); // Emits transfer result

    @ViewChild('userAutocomplete') userAutocomplete!: AutoCompleteComponent;

    accountOwner: IUser | null = null;
    admins: IUser[] = [];
    eligibleUsers: IUser[] = []; // Admins + Account Owner
    filteredUsers = signal<IUser[]>([]);
    selectedUser: IUser | null = null;
    searchQuery: string = '';
    useDefaultTransfer: boolean = true; // Checkbox checked by default
    _isDefaultTransferDisabled: boolean = false; // Internal flag - disable checkbox if current user is SUPER_ADMIN
    currentUser: IUser | null = null;
    loading: boolean = false;
    SVG = TRANSFER_OWNERSHIP

    // Transfer states
    transferState: 'initial' | 'transferring' | 'success' = 'initial';
    transferringAnimationData: any = null;
    successAnimationData: any = null;

    // Autocomplete suggestions
    autocompleteSuggestions = signal<AutoCompleteOption[]>([]);

    constructor(
        private userService: UserService,
        private authService: AuthenticationService,
        private toast: ToastService,
        private http: HttpClient
    ) { }

    ngOnInit() {
        this.loadEligibleUsers();
        this.loadTransferAnimations();
    }

    loadTransferAnimations() {
        // Load transferring animation
        this.http.get('/animations/transferOwnership.json').subscribe({
            next: (data) => {
                this.transferringAnimationData = data;
            },
            error: (error) => {
                console.error('Failed to load transferring animation:', error);
            }
        });

        // Load success animation
        this.http.get('/animations/successfullyUploadedFiles.json').subscribe({
            next: (data) => {
                this.successAnimationData = data;
            },
            error: (error) => {
                console.error('Failed to load success animation:', error);
            }
        });
    }

    loadEligibleUsers() {
        this.loading = true;

        // Get current user to identify account owner
        this.authService.currentUser.subscribe(currentUser => {
            if (!currentUser) {
                this.loading = false;
                return;
            }

            this.currentUser = currentUser;

            // Get admins
            this.userService.getAdmins().subscribe({
                next: (res) => {
                    if (res.success) {
                        this.admins = res.data || [];

                        // Account owner is the user with SUPER_ADMIN role
                        let owner = this.admins.find(u => u.role === USER_ROLE.SUPER_ADMIN);

                        // If not found in admins list, check if current user is the account owner
                        if (!owner && currentUser.role === USER_ROLE.SUPER_ADMIN) {
                            owner = currentUser;
                        }

                        this.accountOwner = owner || null;

                        // If isDefaultTransferDisabled is provided as input, use it
                        // Otherwise, determine it based on whether current user is SUPER_ADMIN and is the account owner
                        if (this.isDefaultTransferDisabled !== undefined) {
                            this._isDefaultTransferDisabled = this.isDefaultTransferDisabled;
                        } else {
                            // Check if current user is SUPER_ADMIN and is the account owner
                            // If so, disable the default transfer checkbox (can't transfer to themselves)
                            if (currentUser.role === USER_ROLE.SUPER_ADMIN &&
                                this.accountOwner &&
                                currentUser._id === this.accountOwner._id) {
                                this._isDefaultTransferDisabled = true;
                            } else {
                                this._isDefaultTransferDisabled = false;
                            }
                        }

                        // Uncheck by default if disabled
                        if (this._isDefaultTransferDisabled) {
                            this.useDefaultTransfer = false;
                        }

                        // Build eligible users list: admins + account owner (if not already in admins)
                        this.eligibleUsers = [...this.admins];
                        if (this.accountOwner && !this.eligibleUsers.some(u => u._id === this.accountOwner!._id)) {
                            this.eligibleUsers.push(this.accountOwner);
                        }

                        // Filter to only include admins and account owner (SUPER_ADMIN)
                        this.eligibleUsers = this.eligibleUsers.filter(u =>
                            u.role === USER_ROLE.ADMIN ||
                            u.role === USER_ROLE.SUPER_ADMIN || u.workspaceRole === SHARE_ACCESS.OWNER
                        );

                        this.filteredUsers.set([...this.eligibleUsers]);
                        this.updateAutocompleteSuggestions();
                        this.loading = false;
                    } else {
                        this.toast.error('Failed to load users');
                        this.loading = false;
                    }
                },
                error: (err) => {
                    this.toast.error('Failed to load users');
                    this.loading = false;
                }
            });
        });
    }

    onSearch(event: any) {
        const query = (event.query || '').toLowerCase().trim();
        if (!query) {
            this.filteredUsers.set([...this.eligibleUsers]);
            this.updateAutocompleteSuggestions();
            return;
        }

        const filtered = this.eligibleUsers.filter(user => {
            const fullName = (user.fullName || `${user.firstName} ${user.lastName}`).toLowerCase();
            const email = (user.email || '').toLowerCase();
            return fullName.includes(query) || email.includes(query);
        });
        this.filteredUsers.set(filtered);
        this.updateAutocompleteSuggestions();
    }

    onUserSelect(user: IUser) {
        if (user) {
            this.selectedUser = user;
            this.useDefaultTransfer = false; // Uncheck the checkbox when user is selected
            this.searchQuery = '';
        }
    }

    removeSelectedUser() {
        this.selectedUser = null;
        // Only check the checkbox if it's not disabled
        this.useDefaultTransfer = !this._isDefaultTransferDisabled;
        this.searchQuery = '';
    }

    onCheckboxChange(checked: any) {
        // Prevent changes if checkbox is disabled
        if (this._isDefaultTransferDisabled) {
            return;
        }
        this.useDefaultTransfer = checked;
        if (checked) {
            this.selectedUser = null;
            this.searchQuery = '';
        }
    }

    getTransferEmail(): string | null {
        if (this.useDefaultTransfer && this.accountOwner) {
            return this.accountOwner.email;
        }
        if (this.selectedUser) {
            return this.selectedUser.email;
        }
        return null;
    }

    getAccountOwnerName(): string {
        if (this.accountOwner) {
            return this.accountOwner.fullName || `${this.accountOwner.firstName} ${this.accountOwner.lastName}`;
        }
        return 'Account Owner';
    }

    getUserRoleLabel(user: IUser): string {
        if (user.role === USER_ROLE.SUPER_ADMIN || user.workspaceRole === SHARE_ACCESS.OWNER) {
            return 'Owner';
        }
        if (user.role === USER_ROLE.ADMIN || user.workspaceRole === SHARE_ACCESS.ADMIN) {
            return 'Admin';
        }
        return 'Member';
    }

    confirmTransfer() {
        const email = this.getTransferEmail();
        if (!email) {
            this.toast.error('Please select a user to transfer ownership to');
            return;
        }

        // If transfer function is provided, use it; otherwise emit event for backward compatibility
        if (this.transferFunction) {
            this.transferState = 'transferring';
            this.transferFunction(email).subscribe({
                next: (res) => {
                    if (res.success) {
                        this.transferState = 'success';
                        this.transferCompleted.emit({ success: true, message: res.message });
                    } else {
                        this.transferState = 'initial';
                        this.toast.error(res.message || 'Failed to transfer ownership');
                        this.transferCompleted.emit({ success: false, message: res.message });
                    }
                },
                error: (err) => {
                    this.transferState = 'initial';
                    this.toast.error(err?.error?.message || 'Failed to transfer ownership');
                    this.transferCompleted.emit({ success: false, message: err?.error?.message });
                }
            });
        } else {
            // Backward compatibility: emit event
            this.transferConfirmed.emit(email);
        }
    }

    cancel() {
        this.visibleChange.emit(false);
        this.resetDialog();
    }

    resetDialog() {
        this.selectedUser = null;
        this.searchQuery = '';
        // Only check the checkbox if it's not disabled
        this.useDefaultTransfer = !this._isDefaultTransferDisabled;
        this.transferState = 'initial';
    }

    onDoneClick() {
        if (this.transferState === 'success') {
            this.visibleChange.emit(false);
            this.resetDialog();
        }
    }

    onDialogVisibleChange(visible: boolean) {
        if (!visible) {
            this.resetDialog();
        }
        this.visibleChange.emit(visible);
    }

    updateAutocompleteSuggestions() {
        // remove the current user from the suggestions
        this.authService.currentUser.subscribe(currentUser => {
            if (currentUser) {
                this.filteredUsers.set(this.filteredUsers().filter(user => user._id !== currentUser._id));
            }
        });
        const suggestions: AutoCompleteOption[] = this.filteredUsers().map(user => ({
            ...user,
            label: user.fullName || `${user.firstName} ${user.lastName}`,
            value: user
        }));
        this.autocompleteSuggestions.set(suggestions);
    }
}

